import React, { useId, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useChartColors } from '../utils/chartColors';

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <div className="chart-tooltip">
      <strong>{label || entry?.name}</strong>
      <span className="chart-tooltip-value">
        {entry?.value ?? entry?.payload?.hours ?? entry?.payload?.matches}
      </span>
    </div>
  );
}

function DashboardChart({ chart, chartIndex = 0 }) {
  const colors = useChartColors();
  const [activeIndex, setActiveIndex] = useState(null);
  const gradId = useId().replace(/:/g, '');

  const tickStyle = { fontSize: 11, fill: colors.text };
  const axisProps = {
    tick: tickStyle,
    axisLine: { stroke: colors.grid },
    tickLine: { stroke: colors.grid },
  };

  if (!chart?.data?.length) {
    return (
      <div className="chart-card chart-card--empty">
        <h3>{chart.title}</h3>
        <p>No data available yet</p>
      </div>
    );
  }

  const getFill = (entry, i) => entry.fill || colors.palette[i % colors.palette.length];
  const seriesColor = colors.palette[chartIndex % colors.palette.length] || colors.primary;
  const seriesAlt = colors.palette[(chartIndex + 2) % colors.palette.length] || colors.primaryLight;

  const renderChart = () => {
    switch (chart.type) {
      case 'pie': {
        // Ring radius resolves against the shorter axis, so a horizontal legend
        // starves it while the card's width goes unused. Move the legend to the
        // side when there are few series and let the ring take the full height.
        const legendVertical = chart.data.length <= 6;
        return (
          <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <Pie
              data={chart.data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="56%"
              outerRadius="86%"
              paddingAngle={2}
              onMouseEnter={(_, i) => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {chart.data.map((entry, i) => (
                <Cell
                  key={entry.name}
                  fill={getFill(entry, i)}
                  stroke={colors.surface}
                  strokeWidth={2}
                  opacity={activeIndex === null || activeIndex === i ? 1 : 0.45}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s ease' }}
                />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
            <Legend
              layout={legendVertical ? 'vertical' : 'horizontal'}
              align={legendVertical ? 'right' : 'center'}
              verticalAlign={legendVertical ? 'middle' : 'bottom'}
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, color: colors.text, lineHeight: 1.8 }}
              formatter={(value) => <span style={{ color: colors.heading }}>{value}</span>}
            />
          </PieChart>
        );
      }

      case 'line': {
        const key = chart.dataKey || 'value';
        const fillId = `line-fill-${gradId}`;
        return (
          <AreaChart data={chart.data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={seriesColor} stopOpacity={0.28} />
                <stop offset="100%" stopColor={seriesColor} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis {...axisProps} width={34} />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ stroke: seriesColor, strokeWidth: 1, strokeDasharray: '4 4' }}
            />
            <Area
              type="monotone"
              dataKey={key}
              stroke={seriesColor}
              strokeWidth={2.5}
              fill={`url(#${fillId})`}
              dot={{ fill: seriesColor, stroke: colors.surface, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: seriesAlt, stroke: colors.surface, strokeWidth: 2 }}
            />
          </AreaChart>
        );
      }

      case 'bar':
      default: {
        const barGradPrefix = `bar-${gradId}`;
        // Rotated ticks cost ~22px of plot height, which only pays off once
        // labels are dense enough to collide.
        const rotateTicks = chart.data.length > 4;
        return (
          <BarChart
            data={chart.data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            barCategoryGap="22%"
          >
            <defs>
              {chart.data.map((entry, i) => {
                const c = getFill(entry, i);
                return (
                  <linearGradient key={entry.name || i} id={`${barGradPrefix}-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={c} stopOpacity={1} />
                    <stop offset="100%" stopColor={c} stopOpacity={0.72} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
            <XAxis
              dataKey="name"
              {...axisProps}
              interval={0}
              angle={rotateTicks ? -20 : 0}
              textAnchor={rotateTicks ? 'end' : 'middle'}
              height={rotateTicks ? 48 : 26}
              tickMargin={rotateTicks ? 6 : 8}
            />
            <YAxis {...axisProps} allowDecimals={false} width={34} />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ fill: `color-mix(in srgb, ${seriesColor} 8%, transparent)` }}
            />
            <Bar
              dataKey="value"
              radius={[6, 6, 0, 0]}
              maxBarSize={56}
              onMouseEnter={(_, i) => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {chart.data.map((entry, i) => (
                <Cell
                  key={entry.name}
                  fill={`url(#${barGradPrefix}-${i})`}
                  opacity={activeIndex === null || activeIndex === i ? 1 : 0.5}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s ease' }}
                />
              ))}
            </Bar>
          </BarChart>
        );
      }
    }
  };

  return (
    <div className="chart-card" style={{ '--chart-accent': seriesColor }}>
      <h3>{chart.title}</h3>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function DashboardCharts({ charts, isLoading }) {
  if (isLoading) {
    return <div className="dashboard-section-loading">Loading analytics...</div>;
  }

  if (!charts?.length) {
    return (
      <div className="dashboard-empty-charts">
        <p>No analytics available for your role yet.</p>
      </div>
    );
  }

  return (
    <div className="charts-grid">
      {charts.map((chart, index) => (
        <DashboardChart key={chart.id} chart={chart} chartIndex={index} />
      ))}
    </div>
  );
}
